import { useEffect, useMemo, useState } from "react";
import { Loading } from "@cpnw/ui";
import { AdminContainer } from "hooks/useAdmin";
import { RequirementContainer } from "hooks/useRequirement";
import StudentListTable from "components/StudentList/StudentListTable";
import DashboardSearch from "components/DashboardSearch";
import ConfirmServiceModal from "components/ConfirmServiceModal";
import styles from "./CpnwWatch.module.css";
import { HeaderSection } from "@cpnw/ui";
import DashboardTablePagination from "components/DashboardTablePagination";
import { ContactCheckStatuses, ContactRequirementStatuses } from "pages/enums";
import { StaticRoutes } from "pages/routes";
import ConfirmSubmissionModal from "components/ConfirmSubmissionModal";
import { format, parseISO } from "date-fns";
import { AccountContainer } from "hooks/useAccount";
import PassportRequirementReviewerView from "components/PassportRequirementReviewerView";
import { Modal } from "@cpnw/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

const CpnwWatch = () => {
  const { studentsWatchData, refetchStudentsWatchData, completeWatchForStudentPost } = AdminContainer.useContainer();
  const {
    contactsToReviewData,
    contactsToReviewLoading,
    refetchContactsToReviewData,
    // postVerificationComplete,
  } = RequirementContainer.useContainer();
  const { userRoles } = AccountContainer.useContainer();
  const [submittedStudents, setSubmittedStudents] = useState([]);
  const [clickedStudent, setClickedStudent] = useState({});
  const [shouldFetchUserWatchRequirement, setShouldFetchUserWatchRequirement] = useState(false);
  const { useStudentRequirementsLazy } = RequirementContainer.useContainer();
  const {
    data: userRequirementsData,
    isLoading: userReqLoading,
    refetch: refetchUserRequirements,
  } = useStudentRequirementsLazy(clickedStudent.ContactIDPK, shouldFetchUserWatchRequirement);

  const [masterLoading, setMasterLoading] = useState(false);
  const roles = userRoles.map((a) => a.toUpperCase());
  const [tempStudents, setTempStudents] = useState([]);
  const [students, setStudents] = useState([]);
  const [studReview, setStudReview] = useState([]);
  const [paired, setPaired] = useState(false);

  const convertApiResponseToStudents = apiResponse => {
    const students = apiResponse?.map(student => {
      const isCPNW = student.WatchCheckTypeStatusId !== 1;
      let status;

      switch (student.WatchCheckTypeStatusId) {
        case ContactCheckStatuses.NotSubmitted:
          break;
        case ContactCheckStatuses.Submitted:
          status = 'waiting submission';
          break;
        case ContactCheckStatuses.RequirementCreated:
          status = 'pending';
          break;
        case ContactCheckStatuses.CheckrRequested:
          status = 'watch report submitted';
          break;
        case ContactCheckStatuses.Resent:
          status = 'resent';
          break;
      }

      return {
        ...student,
        dob: student.DOB,
        middleInitial: student.MidInitial,
        id: student.ContactIDPK,
        name: `${student.NameFirst}, ${student.NameLast}`, // Combine NameFirst and NameLast
        program: student.BusinessUnitName,
        school: student.School,
        isCPNW,
        status
      };
    });

    const cpnwSubmissions = students?.filter(stud => stud.WatchCheckTypeStatusId !== 1);

    // console.log(cpnwSubmissions);

    return cpnwSubmissions;
  };

  const convertApiResponseToRequirements = apiReviewResponse => {
    const revData = apiReviewResponse?.data;

    return revData;
  }

  const pairApiResponseToStudents = () => {
    const mergedStudents = tempStudents.map(student => {
      const match = studReview.find(s => s.ContactId === student.ContactIDPK)

      return {
        ...student,
        reviewData: match || null
      };
    });

    const filteredStatusStudents = mergedStudents.filter(student => {
      const status = student.ContactRequirementStatus;

      //keep if status is null, 1, or 6 (null indicating it hasnt been submitted yet, 1 being its submitted, but not reviewed, 6 being its inreview)
      return status === null || status === ContactRequirementStatuses.NotSubmitted || status === ContactRequirementStatuses.InReview || student.WatchCheckTypeStatusId === ContactCheckStatuses.Resent || student.WatchCheckTypeStatusId === ContactCheckStatuses.Submitted;
    });

    //take submitted students into tracking here.
    const updatedFilteredStudents = filteredStatusStudents.map(student => {
      const match = submittedStudents.find(s => s.ContactIDPK === student.ContactIDPK)

      return {
        ...student,
        ...(match && {
          WatchCheckTypeStatusId: match.WatchCheckTypeStatusId,
          status: match.status
        }
        )
      }
    })

    setStudents(updatedFilteredStudents);
    setPaired(true);
    setMasterLoading(false);
  }

  useEffect(() => {
    // refetchStudents()
    refetchStudentsWatchData();
    refetchContactsToReviewData();
  }, [])

  useEffect(() => {
    if (studentsWatchData && studentsWatchData.length > 0) {
      setTempStudents(convertApiResponseToStudents(studentsWatchData));
    } else {
      setTempStudents([]);
    }
  }, [studentsWatchData]);

  useEffect(() => {
    if (contactsToReviewData && contactsToReviewData.data && contactsToReviewData.data.length > 0) {
      setStudReview(convertApiResponseToRequirements(contactsToReviewData));
    } else {
      setStudReview([]);
    }
  }, [contactsToReviewData]);

  useEffect(() => {
    if (userRequirementsData && userRequirementsData.data.length > 0 && shouldFetchUserWatchRequirement) {
      const requirement = userRequirementsData.data.find(
        (a) => a.ContactRequirementId == clickedStudent.ContactRequirementId
      );

      if (requirement.Category.HasModule) {
        //idk
      } else {
        (requirement.Submissions || []).forEach((sub) => {
          sub.files = (sub.Files || []).map((f) => ({
            id: f.Id,
            name: f.Name,
            url: f.Url,
          }));
        });

        const studentInfo = {
          lastName: clickedStudent.reviewData?.NameLast,
          firstName: clickedStudent.reviewData?.NameFirst,
          previousNames: clickedStudent.reviewData?.PreviousNames,
          school: clickedStudent.reviewData?.Facility,
          program: clickedStudent.reviewData?.Program,
          sidEid: clickedStudent.reviewData?.IndividualId || "",
          dob: clickedStudent.reviewData?.DOB,
          email: clickedStudent.reviewData?.Email,
          phone: clickedStudent.reviewData?.Phone,
          //emergency contact
          eContact: clickedStudent.reviewData?.EmergencyContact,
          eContactPhone: clickedStudent.reviewData?.EmergencyPhone,
        };

        const modalData = {
          contactId: clickedStudent.ContactIDPK,
          contactRequirementId: requirement.ContactRequirementId,
          requirementName: requirement.Name,
          type: requirement.Category,
          abbreviation: requirement.Abbr,
          requiredBy: requirement.RequiredBy.map((a) => a.Name),
          frequency: requirement.Frequency,
          instructions: requirement.Instructions || "",
          status: requirement.Status || "",
          requirementFiles: (requirement.RequirementFiles || []).map((a) => ({
            id: a.Id,
            name: a.Name,
            url: a.Url,
          })),
          messages: requirement.Messages || [],
          allowWaiver: requirement.AllowWaiver,
          isWaived: requirement.IsWaived,
          score: requirement.Score ? requirement.Score.Score : null,
          submissions: requirement.Submissions,
          submission:
            requirement.Submissions.length > 0
              ? requirement.Submissions[0]
              : {},
          hasLicenses: requirement.HasLicenses,
          expiresOn: requirement.ExpiresOn,
          canReview: requirement.CanReview,
          canEdit: requirement.CanEdit,
          canView: requirement.CanView,
          subCategory: requirement.SubCategory,
          isEducationRole: roles.includes("EDUCATION") || roles.includes("FACULTY"),
          createdByTypeContact: requirement.CreatedByTypeContact,
          approvalConditions: requirement.ApprovalConditions,
        };

        // console.log("modalData", modalData);

        setReviewerModalOpen(true);
        setReviewerModalStudentInfo(studentInfo);
        setReviewerModalData(modalData);
      }
      setShouldFetchUserWatchRequirement(false)
    }
  }, [userRequirementsData, userReqLoading, shouldFetchUserWatchRequirement])

  useEffect(() => {
    pairApiResponseToStudents();

    // if (tempStudents.length > 0 && studReview.length > 0) {
    //   if (!paired) {
    //     console.log("pairing");
    //     pairApiResponseToStudents();
    //   }
    // }
  }, [tempStudents, studReview, paired])

  const [cpnwSubmissionViewStud, setCPNWSubmissionViewStud] = useState({}); //student data for cpnw view of table when submission is clicked

  const [items, setItems] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(students.length);
  const [limit, setLimit] = useState(10); // Default limit
  // cpnw confirm reviewer modal state
  const [cpnwReviewerModalOpen, setCPNWReviewerModalOpen] = useState(false);
  // cpnw reviewer modal state
  const [reviewerModalOpen, setReviewerModalOpen] = useState(false);
  // cpnw reviewer modal data state
  const [reviewerModalData, setReviewerModalData] = useState({});
  //cpnw reviewer modal student info
  const [reviewerModalStudentInfo, setReviewerModalStudentInfo] = useState({});

  // Search states
  const [searchStudent, setSearchStudent] = useState("");
  const [searchStatus, setSearchStatus] = useState("");
  const [searchProgram, setSearchProgram] = useState("");
  const [searchAnything, setSearchAnything] = useState("");

  /** Function to filter assignments based on name, status, program, anything */
  const onFilter = (searchStudent, searchStatus, searchProgram, searchAnything) => {
    return students.filter(student => {
      // Convert filter values and assignment properties to lowercase for case-insensitive comparison
      const name = student.name?.toLowerCase();
      const status = student.status?.toLowerCase();
      const program = student.program?.toLowerCase();

      // If searchAnything is provided, check if it matches any of the three fields
      if (searchAnything) {
        const searchValue = searchAnything.toLowerCase();
        if (!name.includes(searchValue) && !status.includes(searchValue) && !program.includes(searchValue)) {
          return false;
        }
      } else {
        // Check if name matches
        if (searchStudent && !name.includes(searchStudent.toLowerCase())) {
          return false;
        }
        // Check if status matches
        if (searchStatus && !status.includes(searchStatus.toLowerCase())) {
          return false;
        }
        // Check if program matches
        if (searchProgram && !program.includes(searchProgram.toLowerCase())) {
          return false;
        }
      }
      // If all conditions pass, return true to include the assignment
      return true;
    });
  };

  const onPageChange = (e) => {
    setCurrentPage(e);
  }

  const onSaveReq = () => {
    // console.log("onsave hit");
    setReviewerModalOpen(false);

    //refetch not actually doing anything??
    setPaired(false);
    refetchStudentsWatchData();
    refetchContactsToReviewData();
  }

  const handleStudentNameClickOuter = (stud) => {
    if (stud.ContactRequirementId && stud.WatchCheckTypeStatusId !== ContactCheckStatuses.NotSubmitted) {
      setClickedStudent(stud);
      setShouldFetchUserWatchRequirement(true);
    }
  }

  const handleOnWatchReportSubmitClickOuter = (stud) => {
    setCPNWReviewerModalOpen(true);
    setCPNWSubmissionViewStud(stud); //state to track which student it is being triggered for
  }

  const handleConfirmWatchCPNWInit = async () => {

    for (var student of items) {
      if (student.id === cpnwSubmissionViewStud.id) {
        let currentSubmitted = submittedStudents;

        currentSubmitted.push({ ...student, WatchCheckTypeStatusId: 4, status: 'pending' });
        console.log("updated items below");
        console.log(currentSubmitted);
        setSubmittedStudents(currentSubmitted);
      }
    }

    //actually post to update status id here on new endpoint
    let studBody = {
      ContactIDPK: cpnwSubmissionViewStud.ContactIDPK,
      WatchCheckTypeStatusId: 4
    };

    const result = await completeWatchForStudentPost(studBody)

    // console.log(result);

    // setItems([...updatedItems]);

    //refetch not actually doing anything??
    setPaired(false);
    refetchStudentsWatchData();
    refetchContactsToReviewData();
  }

  useEffect(() => {
    let filterStudents = onFilter(searchStudent, searchStatus, searchProgram, searchAnything).map(student => {
      const selectedStudent = selectedStudents.find(s => s.id === student.id);
      return selectedStudent ? { ...student, ...selectedStudent } : student;
    });

    const startIndex = (currentPage - 1) * limit;
    const endIndex = Math.min(startIndex + limit - 1, filterStudents.length - 1);
    setItems(filterStudents.slice(startIndex, endIndex + 1));
    setTotalItems(filterStudents.length);
  }, [searchStudent, searchStatus, searchProgram, searchAnything, currentPage, limit, students]);

  useEffect(() => {
    if(userReqLoading && contactsToReviewLoading){
      setMasterLoading(true);
    } else if(!userReqLoading && contactsToReviewLoading){
      setMasterLoading(true);
    } else if(userReqLoading && !contactsToReviewLoading){
      setMasterLoading(true);
    } else {
      setMasterLoading(false);
    }
  }, [userReqLoading, contactsToReviewLoading]);


  return (
    <>
      <HeaderSection header={"CPNW WATCH Report"} />

      {/* Search Container */}
      <div className={styles.searchContainer}>
        <DashboardSearch
          onInput={setSearchStudent}
          showLabel={false}
          placeholder="Search Student"
        />
        <DashboardSearch
          onInput={setSearchStatus}
          showLabel={false}
          placeholder="Search Status"
        />
        <DashboardSearch
          onInput={setSearchProgram}
          showLabel={false}
          placeholder="Search Program"
        />
        <DashboardSearch
          onInput={setSearchAnything}
          showLabel={false}
          placeholder="Search Anything"
        />
      </div>

      {/* Student Table */}
      <div className={styles.loaderContainer}>
        <div className={styles.table}>
          {masterLoading && (
            <div className={styles.loaderOverlay}>
              <Loading />
            </div>
          )}
          <StudentListTable
            view={"cpnw"}
            students={items}
            onWatchReportSubmitClick={handleOnWatchReportSubmitClickOuter}
            onStudentNameClick={handleStudentNameClickOuter}
          // studentStatuses={studentStatuses}
          // onInitiate={handleInitiate}
          // onRadioSelection={handleRadioSelection}
          />
        </div>
        <div className={styles.paginationContainer}>
          <DashboardTablePagination onPageChange={(e) => onPageChange(e)} total={totalItems}
            currentPage={currentPage}
            isAutoSetPage={false}
            perPage={limit} />
        </div>
      </div>


      <Modal
        isOpen={reviewerModalOpen}
        onRequestClose={() => setReviewerModalOpen(false)}
        className={styles.modal_content}
        modalClassName={styles.modal_wrapper}
      >
        <div className={styles.admin_review_modal}>
          <div className={styles.close_modal_btn}>
            <FontAwesomeIcon
              icon={faXmark}
              style={{
                fontSize: "28px",
                transform: "translateY(-15px) translateX(15px)",
                color: "#808080",
                cursor: "pointer",
              }}
              onClick={() => setReviewerModalOpen(false)}
            />
          </div>
          {!userReqLoading ?
            reviewerModalData && (
              <PassportRequirementReviewerView
                studentContactID={clickedStudent.ContactIDPK}
                requirement={reviewerModalData}
                onSaveComplete={() => {
                  // console.log("onsave");
                  onSaveReq();
                }}
                studentInfo={reviewerModalStudentInfo}
              />
            ) : <p>loading</p>
          }
        </div>
      </Modal>

      {/* CPNW Reviewer Wathch Modal */}
      {cpnwReviewerModalOpen && (
        <ConfirmSubmissionModal
          onClose={() => setCPNWReviewerModalOpen(false)}
          onReject={() => {
            // console.log("Cancelled service initiation");
            setCPNWReviewerModalOpen(false);
          }}
          onConfirm={() => {
            // console.log("Submission Initialized!");
            setCPNWReviewerModalOpen(false);
            handleConfirmWatchCPNWInit();
          }}
        />
      )}
    </>
  );
};

export default CpnwWatch;
